import React, { useState, useEffect } from 'react';
import { AutosService } from '../services/autosService';
import { MantenimientoService } from '../services/mantenimientoService';
import { CombustibleService } from '../services/combustibleService';
import './GestionMantenimiento.css';

const GestionMantenimiento = () => {
  const [autos, setAutos] = useState([]);
  const [mantenimientos, setMantenimientos] = useState([]);
  const [consumos, setConsumos] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [formulario, setFormulario] = useState({
    autoId: '',
    fecha: '',
    monto: '',
    taller: '',
    descripcion: '',
    tipoMantenimiento: '',
    kilometraje: '',
    nFactura: ''
  });
  const [filtros, setFiltros] = useState({
    autoId: '',
    fechaInicio: '',
    fechaFin: '',
    tipoMantenimiento: ''
  });
  const [alertas, setAlertas] = useState([]);

  useEffect(() => {
    const unsubscribeAutos = AutosService.suscribirseAutos(setAutos);
    const unsubscribeMantenimientos = MantenimientoService.suscribirseMantenimientos(setMantenimientos);
    const unsubscribeConsumos = CombustibleService.suscribirseRegistros(setConsumos);

    return () => {
      if (typeof unsubscribeAutos === 'function') unsubscribeAutos();
      if (typeof unsubscribeMantenimientos === 'function') unsubscribeMantenimientos();
      if (typeof unsubscribeConsumos === 'function') unsubscribeConsumos();
    };
  }, []);

  useEffect(() => {
    generarAlertasPredictivas();
  }, [autos, mantenimientos, consumos]);

  const generarAlertasPredictivas = () => {
    const nuevasAlertas = [];
    
    // Solo considerar unidades de reparto
    const autosReparto = autos.filter(auto => auto.areaVehicular === 'reparto');
    
    autosReparto.forEach(auto => {
      // Obtener mantenimientos del auto
      const mantenimientosAuto = mantenimientos.filter(m => m.autoId === auto.id);
      const ultimoMantenimiento = mantenimientosAuto
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];

      // Obtener consumos del auto
      const consumosAuto = consumos.filter(c => c.vehiculo.includes(auto.placas));
      
      // Análisis de consumo de combustible
      if (consumosAuto.length >= 3) {
        const rendimientos = consumosAuto.map(c => c.rendimiento || 0);
        const rendimientoPromedio = rendimientos.reduce((a, b) => a + b, 0) / rendimientos.length;
        const ultimoRendimiento = rendimientos[rendimientos.length - 1];
        
        if (ultimoRendimiento < rendimientoPromedio * 0.85) {
          nuevasAlertas.push({
            tipo: 'warning',
            auto: `${auto.placas} - ${auto.marca} ${auto.modelo}`,
            mensaje: `El vehículo ${auto.placas} muestra consumo anormal de combustible (${ultimoRendimiento.toFixed(1)} km/l vs promedio ${rendimientoPromedio.toFixed(1)} km/l). Posible problema en inyectores. Revisión sugerida en 5 días.`,
            prioridad: 'alta',
            icono: '⚠️'
          });
        }
      }

      // Análisis de frecuencia de mantenimiento
      if (mantenimientosAuto.length >= 2) {
        const fechasMantenimiento = mantenimientosAuto
          .map(m => new Date(m.fecha))
          .sort((a, b) => b - a);
        
        const diasDesdeUltimoMantenimiento = Math.floor(
          (new Date() - fechasMantenimiento[0]) / (1000 * 60 * 60 * 24)
        );

        // Si han pasado más de 90 días desde el último mantenimiento
        if (diasDesdeUltimoMantenimiento > 90) {
          nuevasAlertas.push({
            tipo: 'info',
            auto: `${auto.placas} - ${auto.marca} ${auto.modelo}`,
            mensaje: `El vehículo ${auto.placas} no ha tenido mantenimiento en ${diasDesdeUltimoMantenimiento} días. Se recomienda revisión preventiva.`,
            prioridad: 'media',
            icono: '🔧'
          });
        }
      }

      // Análisis de patrones de mantenimiento
      const mantenimientosPreventivos = mantenimientosAuto.filter(m => m.tipoMantenimiento === 'preventivo');
      const mantenimientosCorrectivos = mantenimientosAuto.filter(m => m.tipoMantenimiento === 'correctivo');
      
      if (mantenimientosCorrectivos.length > mantenimientosPreventivos.length) {
        nuevasAlertas.push({
          tipo: 'error',
          auto: `${auto.placas} - ${auto.marca} ${auto.modelo}`,
          mensaje: `El vehículo ${auto.placas} tiene más mantenimientos correctivos (${mantenimientosCorrectivos.length}) que preventivos (${mantenimientosPreventivos.length}). Se recomienda implementar un programa de mantenimiento preventivo.`,
          prioridad: 'alta',
          icono: '🚨'
        });
      }
    });

    setAlertas(nuevasAlertas);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormulario(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await MantenimientoService.crearMantenimiento({
        ...formulario,
        fechaCreacion: new Date().toISOString(),
        timestamp: Date.now()
      });

      alert('Mantenimiento registrado exitosamente');
      setFormulario({
        autoId: '',
        fecha: '',
        monto: '',
        taller: '',
        descripcion: '',
        tipoMantenimiento: '',
        kilometraje: '',
        nFactura: ''
      });
      setMostrarModal(false);
    } catch (error) {
      console.error('Error al guardar:', error);
      alert('Error al guardar el mantenimiento');
    }
  };

  const getAutoSeleccionado = () => {
    return autos.find(auto => auto.id === formulario.autoId);
  };

  const mantenimientosFiltrados = mantenimientos.filter(mantenimiento => {
    if (filtros.autoId && mantenimiento.autoId !== filtros.autoId) return false;
    if (filtros.fechaInicio && mantenimiento.fecha < filtros.fechaInicio) return false;
    if (filtros.fechaFin && mantenimiento.fecha > filtros.fechaFin) return false;
    if (filtros.tipoMantenimiento && mantenimiento.tipoMantenimiento !== filtros.tipoMantenimiento) return false;
    return true;
  });

  const calcularEstadisticas = () => {
    const totalMantenimientos = mantenimientos.length;
    const montoTotal = mantenimientos.reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
    const mantenimientosPreventivos = mantenimientos.filter(m => m.tipoMantenimiento === 'preventivo').length;
    const mantenimientosCorrectivos = mantenimientos.filter(m => m.tipoMantenimiento === 'correctivo').length;

    return {
      totalMantenimientos,
      montoTotal,
      mantenimientosPreventivos,
      mantenimientosCorrectivos
    };
  };

  const estadisticas = calcularEstadisticas();

  return (
    <div className="gestion-mantenimiento">
      {/* Header sin estadísticas */}
      <div className="header-section">
        <h1>Gestión de Mantenimiento</h1>
        <button 
          className="btn-registrar"
          onClick={() => setMostrarModal(true)}
        >
          + Registrar Mantenimiento
        </button>
      </div>

      {/* Panel de Mantenimiento Predictivo */}
      {alertas.length > 0 && (
        <div className="alertas-predictivas">
          <h2>Panel de Mantenimiento Predictivo</h2>
          <div className="alertas-grid">
            {alertas.map((alerta, index) => (
              <div key={index} className={`alerta-item ${alerta.tipo} ${alerta.prioridad}`}>
                <div className="alerta-header">
                  <span className="alerta-auto">{alerta.auto}</span>
                  <span className={`alerta-prioridad ${alerta.prioridad}`}>
                    {alerta.prioridad.toUpperCase()}
                  </span>
                </div>
                <p className="alerta-mensaje">{alerta.mensaje}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filtros-section">
        <h2>Filtros de Búsqueda</h2>
        <div className="filtros-grid">
          <div className="filter-group">
            <label>Vehículo:</label>
            <select 
              value={filtros.autoId}
              onChange={(e) => setFiltros({...filtros, autoId: e.target.value})}
            >
              <option value="">Todos los vehículos</option>
              {autos
                .filter(auto => auto.areaVehicular === 'reparto')
                .map(auto => (
                  <option key={auto.id} value={auto.id}>
                    {auto.placas} - {auto.marca} {auto.modelo}
                  </option>
                ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Fecha Inicio:</label>
            <input 
              type="date"
              value={filtros.fechaInicio}
              onChange={(e) => setFiltros({...filtros, fechaInicio: e.target.value})}
            />
          </div>
          <div className="filter-group">
            <label>Fecha Fin:</label>
            <input 
              type="date"
              value={filtros.fechaFin}
              onChange={(e) => setFiltros({...filtros, fechaFin: e.target.value})}
            />
          </div>
          <div className="filter-group">
            <label>Tipo de Mantenimiento:</label>
            <select 
              value={filtros.tipoMantenimiento}
              onChange={(e) => setFiltros({...filtros, tipoMantenimiento: e.target.value})}
            >
              <option value="">Todos los tipos</option>
              <option value="preventivo">Preventivo</option>
              <option value="correctivo">Correctivo</option>
              <option value="emergencia">Emergencia</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de mantenimientos */}
      <div className="mantenimientos-section">
        <h2>Registros de Mantenimiento ({mantenimientosFiltrados.length})</h2>
        
        {mantenimientosFiltrados.length === 0 ? (
          <div className="no-data">
            <p>No hay mantenimientos que coincidan con los filtros</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="mantenimientos-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Vehículo</th>
                  <th>Tipo</th>
                  <th>Provedor
                
                  </th>
                  <th>Monto</th>
                  <th>Descripción</th>
                  <th>Kilometraje</th>
                </tr>
              </thead>
              <tbody>
                {mantenimientosFiltrados.map((mantenimiento) => {
                  const auto = autos.find(a => a.id === mantenimiento.autoId);
                  return (
                    <tr key={mantenimiento.id}>
                      <td>{new Date(mantenimiento.fecha).toLocaleDateString()}</td>
                      <td>{auto ? `${auto.placas} - ${auto.marca} ${auto.modelo}` : 'N/A'}</td>
                      <td>
                        <span className={`tipo-badge ${mantenimiento.tipoMantenimiento}`}>
                          {mantenimiento.tipoMantenimiento}
                        </span>
                      </td>
                      <td>{mantenimiento.taller}</td>
                      <td>${parseFloat(mantenimiento.monto).toLocaleString()}</td>
                      <td className="descripcion-cell">{mantenimiento.descripcion}</td>
                      <td>{mantenimiento.kilometraje ? `${mantenimiento.kilometraje.toLocaleString()} km` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de registro */}
      {mostrarModal && (
        <div className="modal-overlay" onClick={() => setMostrarModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registrar Mantenimiento</h2>
              <button className="close-button" onClick={() => setMostrarModal(false)}>×</button>
            </div>
            <form className="form-mantenimiento" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Vehículo *</label>
                  <select
                    name="autoId"
                    value={formulario.autoId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar vehículo...</option>
                    {autos
                      .filter(auto => auto.areaVehicular === 'reparto')
                      .map(auto => (
                        <option key={auto.id} value={auto.id}>
                          {auto.placas} - {auto.marca} {auto.modelo}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    name="fecha"
                    value={formulario.fecha}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Mantenimiento *</label>
                  <select
                    name="tipoMantenimiento"
                    value={formulario.tipoMantenimiento}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar tipo...</option>
                    <option value="preventivo">Preventivo</option>
                    <option value="correctivo">Correctivo</option>
                    <option value="emergencia">Emergencia</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Monto ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="monto"
                    placeholder="0.00"
                    value={formulario.monto}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Proveedor *</label>
                  <input
                    type="text"
                    name="taller"
                    placeholder="Nombre del proveedor"
                    value={formulario.taller}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>N-factura *</label>
                  <input
                    type="text"
                    name="nFactura"
                    placeholder="Ej: FAC-001234"
                    value={formulario.nFactura}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kilometraje</label>
                  <input
                    type="number"
                    name="kilometraje"
                    placeholder="Kilometraje actual"
                    value={formulario.kilometraje}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Descripción del Mantenimiento *</label>
                <textarea
                  name="descripcion"
                  placeholder="Describe qué se hizo en el mantenimiento..."
                  value={formulario.descripcion}
                  onChange={handleInputChange}
                  rows="4"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancelar" onClick={() => setMostrarModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-registrar">
                  Registrar Mantenimiento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionMantenimiento;


